import React, { useEffect, useState } from 'react';
import mobile from 'is-mobile';
import { Controller, useForm } from 'react-hook-form';
import ReactPaginate from 'react-paginate';
import { Article, Category } from 'constants/blog';
import { getBlogCategoryPageUrl } from 'constants/pages';
import { getAllPosts } from 'services/server/sanity/groq/getAllPosts';
import { toSlug } from 'utils/shared/string/generateSlug';
import ArrowLeft from 'svg/ArrowLeft';
import ArrowRight from 'svg/ArrowRight';
import CategorySelect from 'components/CategorySelect';
import Link from 'components/Link';
import LoadingSkeleton from 'components/LoadingSkeleton';
import Post from 'components/blog/Post';
import classNames from 'styles/utils/classNames';
import { Pagination } from './styles';

export const PAGE_SIZE = 12;

const defaultFilters = [
  {
    _id: 'all',
    title: 'View all',
    count: 100,
  },
];

export default function PostsList({
  articles,
  categories,
  articlesCount,
}: {
  articles: Article[];
  categories: Category[];
  articlesCount: number;
}) {
  const [page, setPage] = useState(0);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>(articles);
  const [mappedCategories, setMappedCategories] = useState<Category[]>([]);

  const [loading, setLoading] = useState(false);
  const [postCount, setPostCount] = useState(articlesCount);
  const [triggerCategoryUpdate, setTriggerCategoryUpdate] = useState(0);
  const isMobile = mobile({
    tablet: true,
  });

  const { control, resetField, watch, setValue, getValues, reset } = useForm({
    defaultValues: {
      [defaultFilters[0]._id]: true,
    },
  });

  const getLatestPosts = async (limit: number, offset: number, pagination = false) => {
    setLoading(true);
    const values = getValues();
    const filters = Object.keys(values).filter((key) => !!values[key]);
    const { posts, totalCount } = await getAllPosts(
      limit,
      filters.length === 1 && filters[0] === 'all' && pagination === false ? 1 : offset,
      filters,
    );

    setFilteredArticles(posts);
    setPostCount(totalCount);
    setLoading(false);
  };

  useEffect(() => {
    let mapArticlesWithCategories = categories.map((filter) => ({
      _id: filter._id,
      value: filter._id,
      title: filter.title,
      count: filter.count,
    }));
    setMappedCategories(mapArticlesWithCategories);
    setFilteredArticles(articles);
  }, []);

  // Re-fetch posts whenever the user toggles a category button. Without this
  // the buttons highlighted but the article grid below never updated.
  useEffect(() => {
    if (triggerCategoryUpdate === 0) return;
    setPage(0);
    getLatestPosts(PAGE_SIZE, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerCategoryUpdate]);

  const filterIds = getValues();

  const CardLoadingSkeleton = () => {
    return (
      <div className="w-full flex-shrink-0 overflow-hidden rounded-lg lg:max-w-[26rem]">
        <LoadingSkeleton
          height="27rem"
          className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
        />
        <div className="mt-2.5">
          <LoadingSkeleton count={3} />
        </div>
      </div>
    );
  };

  const loadingSkeletonGrid = [
    CardLoadingSkeleton,
    CardLoadingSkeleton,
    CardLoadingSkeleton,
    CardLoadingSkeleton,
    CardLoadingSkeleton,
    CardLoadingSkeleton,
    CardLoadingSkeleton,
    CardLoadingSkeleton,
    CardLoadingSkeleton,
    CardLoadingSkeleton,
    CardLoadingSkeleton,
    CardLoadingSkeleton,
  ];

  return (
    <div className="mx-auto max-w-[1200px] pb-24">
      <div className="mb-5 block sm:hidden">
        <CategorySelect
          options={categories.map((filter) => ({
            value: filter._id,
            label: filter.title,
          }))}
        />
      </div>
      <div className="mb-16 hidden gap-2 sm:flex">
        {[...defaultFilters, ...mappedCategories].map(({ title, _id, count }) => {
          const isAllFilter = _id === defaultFilters[0]._id;

          return (
            <Controller
              key={_id}
              control={control}
              name={_id}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <label
                  htmlFor={_id}
                  className={classNames(
                    'typography-product-chips-filters flex h-11 w-32 cursor-pointer items-center justify-center rounded-md',
                    filterIds[_id]
                      ? 'bg-color-bg-lightmode-invert text-color-text-lightmode-invert dark:bg-color-bg-darkmode-invert dark:text-color-text-darkmode-invert'
                      : 'bg-color-bg-lightmode-secondary text-center dark:bg-color-bg-darkmode-secondary',
                    count === 0 ? 'opacity-50' : 'opacity-100',
                  )}
                  key={_id}
                >
                  <input
                    className="hidden"
                    type="checkbox"
                    name={_id}
                    disabled={count === 0}
                    ref={ref}
                    onBlur={onBlur}
                    checked={value === undefined ? false : value}
                    id={_id}
                    onChange={(e) => {
                      setTriggerCategoryUpdate(Math.floor(Math.random() * 100) + 1);
                      if (isAllFilter) {
                        reset();
                      } else {
                        setValue(defaultFilters[0]._id, false);
                      }

                      onChange(e);
                    }}
                  />
                  <div className="checkmark">{title}</div>
                </label>
              )}
            />
          );
        })}
      </div>
      {mappedCategories.length > 0 && (
        <nav
          aria-label="Browse blog by category"
          className="mb-12 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
        >
          <span className="font-semibold text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Topic hubs:
          </span>
          {mappedCategories
            .filter((cat) => cat.title && cat.count > 0)
            .map((cat) => (
              <Link
                key={cat._id}
                href={getBlogCategoryPageUrl(toSlug(cat.title))}
                className="hover:text-color-brand-primary hover:underline"
              >
                {cat.title}
              </Link>
            ))}
        </nav>
      )}
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {!loading ? (
          <>
            {filteredArticles.map((article: Article) => {
              return (
                <Post
                  id={article._id}
                  slug={article.slug}
                  key={article._id}
                  tags={article?.categories?.map((category) => category?.title || '')}
                  title={article.title}
                  createdAt={article.publishedAt}
                  author={article.author}
                  previewText={article.previewDescription}
                  image={article?.mainImage}
                />
              );
            })}
          </>
        ) : (
          loadingSkeletonGrid.map((SkeletonComponent, index) => <SkeletonComponent key={index} />)
        )}
      </div>
      <Pagination className={isMobile ? 'mobile' : 'mt-16'}>
        <ReactPaginate
          disabledClassName="disabled"
          breakLabel={isMobile ? '' : '...'}
          nextLabel={
            <>
              {!isMobile ? 'Next' : ''}
              <ArrowRight className="w-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
            </>
          }
          onPageChange={(selectedItem) => {
            setPage(selectedItem.selected);
            getLatestPosts(PAGE_SIZE, PAGE_SIZE * selectedItem.selected, true);
          }}
          pageRangeDisplayed={isMobile ? 0 : 2}
          marginPagesDisplayed={isMobile ? 1 : 2}
          pageCount={postCount / PAGE_SIZE}
          previousLabel={
            <>
              <ArrowLeft className="w-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />{' '}
              {!isMobile ? 'Previous' : ''}
            </>
          }
        />
      </Pagination>
    </div>
  );
}
